import React from 'react';
import {
  Switch,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  FormHelperText,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import BaseField from './BaseField';
import TextInputField from './TextInputField';
import NumberWithButtonField from './NumberWithButtonField';
import SelectField from './SelectField';
import AdvancedField from './AdvancedField';
import RadioField from './RadioField';
import CustomSelectField from './CustomSelectField';
import FileUploadField from './FileUploadField';
import FileField from './FileField';
import ArrayField from './ArrayField';
import PaymentScheduleField from './PaymentScheduleField';
import FirmAutocompleteField from './FirmAutocompleteField';
import DocumentAssignmentsField from './DocumentAssignmentsField';
import { formFieldStyles } from './FormField.styles';
import { parseDateForForm } from '../../utils/dateUtils';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

const FormField = ({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  formData = {},
  entityId = null,
  ...props
}) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language === 'tr' ? tr : enUS;

  const handleChange = (event, newValue) => {
    let changedValue;

    if (field.type === 'switch' || field.type === 'checkbox') {
      changedValue = event.target.checked;
    } else if (field.type === 'autocomplete' || field.type === 'multiselect') {
      changedValue = newValue;
    } else if (field.type === 'date') {
      changedValue = newValue;
    } else {
      changedValue = event.target.value;
    }

    if (onChange) {
      onChange(field.name, changedValue, event);
    }
  };

  const handleBlur = event => {
    if (onBlur) {
      onBlur(field.name, value, event);
    }
  };

  const renderField = () => {
    const fieldProps = { field, value, onChange, onBlur, error, disabled, formData };
    if (
      [
        'text',
        'email',
        'password',
        'tel',
        'url',
        'number',
        'textarea',
      ].includes(field.type)
    ) {
      return <TextInputField {...fieldProps} />;
    }

    // Number input + "Tutar Getir" gibi yandan buton. field.onFetch(formData)
    // çağrılır, dönen sayısal değer alana yazılır. Hesap tutuk olursa kullanıcı
    // elle de düzenleyebilir.
    if (field.type === 'numberWithButton') {
      return <NumberWithButtonField {...fieldProps} />;
    }

    if (['select', 'autocomplete', 'multiselect'].includes(field.type)) {
      return <SelectField {...fieldProps} />;
    }

    if (field.type === 'radio') {
      const { formData, ...radioProps } = fieldProps;
      return <RadioField {...radioProps} />;
    }

    if (field.type === 'customselect') {
      const { formData, ...customSelectProps } = fieldProps;
      return <CustomSelectField {...customSelectProps} />;
    }

    if (field.type === 'fileupload') {
      const { formData, ...fileUploadProps } = fieldProps;
      return <FileUploadField {...fileUploadProps} entityId={entityId} />;
    }

    if (field.type === 'array') {
      const { formData, ...arrayProps } = fieldProps;
      return <ArrayField {...arrayProps} formData={formData} entityId={entityId} />;
    }

    if (field.type === 'paymentSchedule') {
      return <PaymentScheduleField {...fieldProps} formData={formData} paymentPlanTemplates={field.paymentPlanTemplates} field={field} entityId={entityId} />;
    }

    if (field.type === 'firmSuggestion') {
      return <FirmAutocompleteField {...fieldProps} />;
    }

    // Drag&drop + preview'li tek dosya alani (DocumentBank uploader gibi).
    // AdvancedField'in kalan `file` case'i kullanilmiyor; type: 'file' artik
    // FileField'a yonlendiriliyor.
    if (field.type === 'file') {
      return <FileField {...fieldProps} />;
    }

    // Sirket/Grup/Ekip/Kisi atama listesi (DocumentBank). Hook'lari icinde
    // cagirdigi icin form config'te ekstra props gerekmez.
    if (field.type === 'documentAssignments') {
      return <DocumentAssignmentsField {...fieldProps} />;
    }

    // Buton grubu — select yerine kullanilan gorsel toggle. field.options:
    // [{ value, labelKey, icon? }]. Aktif secim primary highlight aliyor.
    // Genis full-width iki-secenek radio-card gorunumu.
    if (field.type === 'buttonGroup') {
      const opts = Array.isArray(field.options) ? field.options : [];
      const currentValue = value ?? field.defaultValue ?? opts[0]?.value;
      return (
        <div style={{ width: '100%' }}>
          {field.label && (
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: 0.6,
              textTransform: 'uppercase', color: 'rgba(0,0,0,0.6)',
              marginBottom: 8,
            }}>
              {typeof field.label === 'string' && field.label.includes('.') ? t(field.label) : field.label}
            </div>
          )}
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${opts.length}, 1fr)`,
            gap: 8,
            padding: 4,
            borderRadius: 14,
            background: '#f1f5f9',
            border: '1px solid #e2e8f0',
          }}>
            {opts.map((opt) => {
              const active = String(currentValue) === String(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(field.name, opt.value)}
                  style={{
                    padding: '14px 20px',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    border: '1.5px solid',
                    borderColor: active ? '#2563eb' : 'transparent',
                    borderRadius: 10,
                    background: active ? '#ffffff' : 'transparent',
                    color: active ? '#1e293b' : '#64748b',
                    transition: 'all 0.15s ease',
                    opacity: disabled ? 0.55 : 1,
                    boxShadow: active
                      ? '0 2px 8px rgba(37,99,235,0.15), 0 1px 3px rgba(15,23,42,0.06)'
                      : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: 8,
                    letterSpacing: 0.2,
                  }}
                  onMouseEnter={(e) => {
                    if (!disabled && !active) {
                      e.currentTarget.style.color = '#334155';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!disabled && !active) {
                      e.currentTarget.style.color = '#64748b';
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {/* Aktif secime kucuk mavi nokta */}
                  {active && (
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#2563eb', display: 'inline-block',
                    }} />
                  )}
                  {opt.labelKey ? t(opt.labelKey) : opt.label}
                </button>
              );
            })}
          </div>
          {error && (
            <div style={{ fontSize: 12, color: '#d32f2f', marginTop: 6 }}>{error}</div>
          )}
        </div>
      );
    }

    if (
      [
        'color',
        'range',
        'rating',
        'tags',
        'time',
        'datetime-local',
        'month',
        'week',
      ].includes(field.type)
    ) {
      // AdvancedField artık formData'ya da ihtiyaç duyabiliyor (örn. showTime gibi dinamik özellikler için)
      return <AdvancedField {...fieldProps} />;
    }

    if (field.type === 'phone') {
      return (
        <div style={{ width: '100%' }}>
          <PhoneInput
            country={'tr'}
            value={value || ''}
            onChange={phone => onChange(field.name, phone)}
            inputProps={{
              name: field.name,
              required: field.required,
              autoFocus: field.autoFocus,
              disabled: disabled,
              onBlur: handleBlur,
              placeholder: field.placeholder ? t(field.placeholder) : '',
            }}
            inputStyle={{ width: '100%' }}
            specialLabel={field.label ? t(field.label) : ''}
            masks={{ tr: '(...) ... .. ..' }}
            enableSearch={true}
            disableDropdown={false}
            countryCodeEditable={false}
          />
          {error && (
            <FormHelperText error>{error}</FormHelperText>
          )}
        </div>
      );
    }

    const baseProps = {
      disabled,
      fullWidth: true,
      sx: formFieldStyles.field,
    };

    switch (field.type) {
      case 'switch':
        return (
          <FormControlLabel
            control={
              <Switch
                checked={!!value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
              />
            }
            label={field.label ? t(field.label) : ''}
            sx={formFieldStyles.switchField}
          />
        );

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={!!value}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={disabled}
              />
            }
            label={field.label ? t(field.label) : ''}
            sx={formFieldStyles.checkboxField}
          />
        );

      case 'radio':
        return (
          <FormControl component="fieldset" {...baseProps}>
            <FormLabel component="legend">
              {field.label ? t(field.label) : ''}
            </FormLabel>
            <RadioGroup
              value={value || ''}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              {field.options?.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={option.labelKey ? t(option.labelKey) : option.label}
                />
              ))}
            </RadioGroup>
            {error && <FormHelperText error>{error}</FormHelperText>}
          </FormControl>
        );

      case 'date':
        return (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={locale}
          >
            <DatePicker
              label={field.label ? t(field.label) : ''}
              value={parseDateForForm(value)}
              onChange={newValue => handleChange(null, newValue)}
              onBlur={handleBlur}
              disabled={disabled}
              // Bug 3 fix: explicit DD.MM.YYYY format enables MUI's built-in input
              // mask so users can type "12.02.2026" directly into the field.
              format="dd.MM.yyyy"
              // Bug 2 fix: expose month/year views so the calendar header label
              // is clickable to switch between day ↔ month ↔ year pickers.
              views={['year', 'month', 'day']}
              openTo="day"
              slotProps={{
                textField: {
                  ...baseProps,
                  placeholder: field.placeholder
                    ? t(field.placeholder)
                    : 'gg.aa.yyyy',
                },
                popper: {
                  placement: 'bottom-start', // Aşağıya açılsın
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    },
                  ],
                },
              }}
            />
          </LocalizationProvider>
        );

      default:
        return (
          <TextInputField
            field={field}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={error}
            disabled={disabled}
          />
        );
    }
  };

  return <BaseField field={field}>{renderField()}</BaseField>;
};

export default FormField;
